// frontend-react/src/components/sales/PaymentModeSelector.jsx
import React, { useMemo, useState, useEffect, useRef } from 'react';
import PillToggle from './PillToggle.jsx';
import MethodFields from './MethodFields.jsx';
import { paymentMethodLabel, providerLabel } from '../../constants/payment.js';

function toYYYYMMDD(d) {
  const dt = d instanceof Date ? d : new Date(d);
  if (Number.isNaN(dt.getTime())) return '';
  return dt.toISOString().slice(0, 10);
}

function addDays(date, n) {
  const d = new Date(date);
  d.setDate(d.getDate() + n);
  return d;
}

function addMonths(date, n) {
  const d = new Date(date);
  const day = d.getDate();
  d.setMonth(d.getMonth() + n);
  if (d.getDate() < day) d.setDate(0);
  return d;
}

function clampInt(n, min, max, fallback) {
  const x = Number(n);
  if (!Number.isFinite(x)) return fallback;
  const y = Math.trunc(x);
  return Math.max(min, Math.min(max, y));
}

/**
 * splitAmount - FCFA/XOF sans décimales
 */
function splitAmount(total, count) {
  const totalInt = Math.round(Number(total));
  const base = Math.floor(totalInt / count);
  let rest = totalInt - base * count;
  const out = new Array(count).fill(base);
  let i = 0;
  while (rest > 0) {
    out[i] += 1;
    rest -= 1;
    i += 1;
    if (i === count) i = 0;
  }
  return out;
}

function fmtXof(n) {
  return Number(n || 0).toLocaleString('fr-FR');
}

export default function PaymentModeSelector({ totalTTC = 0, value, onChange }) {
  const defaultsCash = {
    method: 'cash',
    provider: 'orange_money',
    reference: '',
    note: '',
    paid_now: true,
  };

  const defaultsInst = {
    installments_count: 3,
    frequency: 'monthly',
    first_due_date: toYYYYMMDD(new Date(Date.now() + 7 * 86400000)),
    custom_schedule: [],
    planned_method: 'cash',
    planned_provider: 'orange_money',
    planned_reference: '',
    planned_note: '',
    initial_paid_now: false,
    initial_amount: '',
    initial_method: 'cash',
    initial_provider: 'orange_money',
    initial_reference: '',
    initial_note: '',
  };

  const isInstallments = value?.type === 'installments';
  const currentCash = { ...defaultsCash, ...(value?.type === 'cash' ? (value.options || {}) : {}) };
  const currentInst = { ...defaultsInst, ...(isInstallments ? (value.options || {}) : {}) };

  const lastCashRef = useRef(currentCash);
  const lastInstRef = useRef(currentInst);

  useEffect(() => { lastCashRef.current = currentCash; }, [currentCash]);
  useEffect(() => { lastInstRef.current = currentInst; }, [currentInst]);

  function setMode(next) {
    if (next === 'cash') onChange?.({ type: 'cash', options: lastCashRef.current });
    else onChange?.({ type: 'installments', options: lastInstRef.current });
  }

  function patchCash(p) {
    onChange?.({ type: 'cash', options: { ...currentCash, ...p } });
  }
  function patchInst(p) {
    onChange?.({ type: 'installments', options: { ...currentInst, ...p } });
  }

  const autoSchedule = useMemo(() => {
    if (!isInstallments || !totalTTC) return [];
    const n = clampInt(currentInst.installments_count, 1, 60, 3);
    const freq = String(currentInst.frequency || 'monthly').toLowerCase();
    const start = new Date(currentInst.first_due_date);

    if (Number.isNaN(start.getTime())) return [];
    const amounts = splitAmount(totalTTC, n);

    const dates = [];
    for (let i = 0; i < n; i += 1) {
      let due = start;
      if (freq === 'weekly') due = addDays(start, 7 * i);
      else if (freq === 'biweekly') due = addDays(start, 14 * i);
      else due = addMonths(start, i);
      dates.push(toYYYYMMDD(due));
    }

    return dates.map((d, i) => ({
      index: i + 1,
      due_date: d,
      amount: amounts[i],
    }));
  }, [isInstallments, currentInst.installments_count, currentInst.frequency, currentInst.first_due_date, totalTTC]);

  const effectiveSchedule = useMemo(() => {
    if (!isInstallments) return [];
    const freq = String(currentInst.frequency || 'monthly').toLowerCase();
    if (freq !== 'custom') return autoSchedule;

    const cs = Array.isArray(currentInst.custom_schedule) ? currentInst.custom_schedule : [];
    return cs
      .map((it, idx) => ({
        index: idx + 1,
        due_date: toYYYYMMDD(it.due_date),
        amount: Math.round(Number(it.amount || 0)),
      }))
      .filter(it => it.due_date);
  }, [isInstallments, currentInst.frequency, currentInst.custom_schedule, autoSchedule]);

  const firstInstallmentAmount = useMemo(() => {
    const v = effectiveSchedule?.[0]?.amount;
    return Number.isFinite(Number(v)) ? Math.round(Number(v)) : 0;
  }, [effectiveSchedule]);

  useEffect(() => {
    if (!isInstallments) return;
    const freq = String(currentInst.frequency || 'monthly').toLowerCase();
    if (freq !== 'custom') return;

    const cs = Array.isArray(currentInst.custom_schedule) ? currentInst.custom_schedule : [];
    if (cs.length > 0) return;

    patchInst({ custom_schedule: autoSchedule.map(r => ({ due_date: r.due_date, amount: r.amount })) });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInstallments, currentInst.frequency]);

  function setCustomLine(idx, patch) {
    const cs = Array.isArray(currentInst.custom_schedule) ? [...currentInst.custom_schedule] : [];
    if (!cs[idx]) cs[idx] = { due_date: '', amount: 0 };
    cs[idx] = { ...cs[idx], ...patch };
    patchInst({ custom_schedule: cs });
  }

  function resetCustomFromAuto() {
    patchInst({ custom_schedule: autoSchedule.map(r => ({ due_date: r.due_date, amount: r.amount })) });
  }

  const customSum = useMemo(() => {
    if (!isInstallments) return 0;
    const freq = String(currentInst.frequency || 'monthly').toLowerCase();
    if (freq !== 'custom') return 0;
    return effectiveSchedule.reduce((s, r) => s + Math.round(Number(r.amount || 0)), 0);
  }, [isInstallments, currentInst.frequency, effectiveSchedule]);

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4 mt-4 shadow-sm">
      <div className="flex items-center justify-between gap-3">
        <div>
          <div className="font-semibold">Paiement</div>
          <div className="text-xs text-slate-500">
            Choisis le mode et la méthode (utile pour la facture / reçu).
          </div>
        </div>

        <PillToggle
          value={isInstallments ? 'installments' : 'cash'}
          onChange={v => setMode(v === 'installments' ? 'installments' : 'cash')}
          options={[
            { value: 'cash', label: 'Comptant' },
            { value: 'installments', label: 'Tranches' },
          ]}
        />
      </div>

      {!isInstallments && (
        <div className="mt-4 space-y-3">
          <MethodFields title="Méthode de paiement" value={currentCash} onChange={patchCash} />

          <div className="border border-slate-200 rounded-xl p-3 bg-slate-50">
            <label className="inline-flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={!!currentCash.paid_now}
                onChange={e => patchCash({ paid_now: e.target.checked })}
              />
              Marquer comme payé maintenant
            </label>
            <div className="text-xs text-slate-500 mt-1">
              Si activé, le backend pourra enregistrer un paiement et la facture affichera "Payé".
            </div>
          </div>

          <div className="mt-1 bg-slate-50 border border-slate-200 rounded-xl p-3 text-sm">
            <div className="flex justify-between">
              <span>Total à payer</span>
              <span className="font-semibold">{fmtXof(totalTTC)} FCFA</span>
            </div>
            <div className="flex justify-between text-slate-500 text-xs mt-1">
              <span>Résumé</span>
              <span>
                {paymentMethodLabel(currentCash.method)}
                {String(currentCash.method).toLowerCase() === 'mobile_money'
                  ? ` (${providerLabel(currentCash.provider)})`
                  : ''}
              </span>
            </div>
          </div>
        </div>
      )}

      {isInstallments && (
        <div className="mt-4 space-y-4">
          <MethodFields
            title="Méthode par défaut (prévue)"
            value={{
              method: currentInst.planned_method,
              provider: currentInst.planned_provider,
              reference: currentInst.planned_reference,
              note: currentInst.planned_note,
            }}
            onChange={(v) =>
              patchInst({
                planned_method: v.method,
                planned_provider: v.provider,
                planned_reference: v.reference,
                planned_note: v.note,
              })
            }
          />

          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div>
              <label className="text-sm">Nombre d'échéances</label>
              <input
                type="number"
                min={1}
                max={60}
                value={currentInst.installments_count}
                onChange={e => patchInst({ installments_count: clampInt(e.target.value, 1, 60, 3) })}
                className="border border-slate-200 rounded-lg px-2 py-2 w-full focus:ring-2 focus:ring-slate-300 focus:border-slate-400"
              />
            </div>

            <div>
              <label className="text-sm">Fréquence</label>
              <select
                value={currentInst.frequency}
                onChange={e => patchInst({ frequency: e.target.value })}
                className="border border-slate-200 rounded-lg px-2 py-2 w-full bg-white focus:ring-2 focus:ring-slate-300 focus:border-slate-400"
              >
                <option value="weekly">Hebdomadaire</option>
                <option value="biweekly">Bi-hebdomadaire</option>
                <option value="monthly">Mensuelle</option>
                <option value="custom">Custom (dates/montants)</option>
              </select>
            </div>

            <div>
              <label className="text-sm">1ère échéance</label>
              <input
                type="date"
                value={currentInst.first_due_date}
                onChange={e => patchInst({ first_due_date: e.target.value })}
                className="border border-slate-200 rounded-lg px-2 py-2 w-full focus:ring-2 focus:ring-slate-300 focus:border-slate-400"
              />
            </div>

            <div className="flex items-end">
              {String(currentInst.frequency).toLowerCase() === 'custom' && (
                <button
                  type="button"
                  onClick={resetCustomFromAuto}
                  className="px-3 py-2 border border-slate-200 rounded-lg w-full hover:bg-slate-50"
                >
                  Réinitialiser (auto)
                </button>
              )}
            </div>
          </div>

          <div className="border border-slate-200 rounded-xl overflow-hidden">
            <div className="grid grid-cols-3 bg-slate-50 text-xs font-semibold">
              <div className="px-3 py-2">#</div>
              <div className="px-3 py-2">Date</div>
              <div className="px-3 py-2 text-right">Montant</div>
            </div>

            {effectiveSchedule.map((row, idx) => {
              const isCustom = String(currentInst.frequency).toLowerCase() === 'custom';
              return (
                <div key={row.index} className="grid grid-cols-3 border-t border-slate-200 text-sm items-center bg-white">
                  <div className="px-3 py-2">{row.index}</div>

                  <div className="px-3 py-2">
                    {isCustom ? (
                      <input
                        type="date"
                        value={row.due_date}
                        onChange={e => setCustomLine(idx, { due_date: e.target.value })}
                        className="border border-slate-200 rounded-lg px-2 py-2 w-full focus:ring-2 focus:ring-slate-300 focus:border-slate-400"
                      />
                    ) : (
                      <span className="text-content-primary">{row.due_date}</span>
                    )}
                  </div>

                  <div className="px-3 py-2 text-right">
                    {isCustom ? (
                      <input
                        type="number"
                        step="1"
                        value={Math.round(Number(row.amount || 0))}
                        onChange={e => setCustomLine(idx, { amount: Math.round(Number(e.target.value || 0)) })}
                        className="border border-slate-200 rounded-lg px-2 py-2 w-full text-right focus:ring-2 focus:ring-slate-300 focus:border-slate-400"
                      />
                    ) : (
                      <span className="text-content-primary">{fmtXof(row.amount)} FCFA</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {String(currentInst.frequency).toLowerCase() === 'custom' && (
            <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 text-sm">
              <div className="flex justify-between">
                <span>Total tranches</span>
                <span className="font-semibold">{fmtXof(customSum)} FCFA</span>
              </div>
              <div className="flex justify-between text-slate-500">
                <span>Total TTC</span>
                <span>{fmtXof(Math.round(Number(totalTTC || 0)))} FCFA</span>
              </div>
              {customSum !== Math.round(Number(totalTTC || 0)) && (
                <div className="mt-1 text-red-600">
                  Attention: La somme des tranches doit être égale au Total TTC.
                </div>
              )}
            </div>
          )}

          {/* Paiement initial (optionnel) */}
          <div className="border border-slate-200 rounded-xl p-4 bg-slate-50">
            <label className="inline-flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={!!currentInst.initial_paid_now}
                onChange={e => {
                  const checked = e.target.checked;
                  const next = { initial_paid_now: checked };
                  if (checked && !currentInst.initial_amount) {
                    next.initial_amount = firstInstallmentAmount || '';
                  }
                  patchInst(next);
                }}
              />
              Le client paie une 1ère tranche maintenant
            </label>
            <div className="text-xs text-slate-500 mt-1">
              Cette option enregistre un paiement immédiat sur la première échéance.
            </div>

            {currentInst.initial_paid_now && (
              <div className="mt-3 space-y-3">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="text-sm">Montant payé maintenant</label>
                    <input
                      type="number"
                      step="1"
                      value={currentInst.initial_amount}
                      onChange={e => patchInst({ initial_amount: Math.round(Number(e.target.value || 0)) })}
                      className="border border-slate-200 rounded-lg px-2 py-2 w-full focus:ring-2 focus:ring-slate-300 focus:border-slate-400"
                    />
                    <div className="text-xs text-slate-500 mt-1">
                      Par défaut: 1ère échéance ({fmtXof(firstInstallmentAmount)} FCFA)
                    </div>
                  </div>
                </div>

                <MethodFields
                  title="Méthode du paiement initial"
                  value={{
                    method: currentInst.initial_method,
                    provider: currentInst.initial_provider,
                    reference: currentInst.initial_reference,
                    note: currentInst.initial_note,
                  }}
                  onChange={(v) =>
                    patchInst({
                      initial_method: v.method,
                      initial_provider: v.provider,
                      initial_reference: v.reference,
                      initial_note: v.note,
                    })
                  }
                />
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
